import { Integration } from "@redotech/redo-model/advanced-flow/integrations";
import {
  SegmentDataStructure,
  segmentDataStructureTypeToDataStructure,
} from "@redotech/redo-model/marketing/segments/segment-data-structures";
import {
  CustomerCharacteristicType,
  SegmentConditionCategory,
} from "@redotech/redo-model/marketing/segments/segment-types";
import { memo, useMemo } from "react";
import { segmentCategoryToFriendly } from "../segment-category";
import { SegmentFieldSelectModal } from "../segment-field-select-modal";
import { Characteristic, schemaToCategories } from "./customer-characteristics";

interface SegmentCharacteristicSelectModalProps {
  isOpen: boolean;
  onModalCloseRequested: () => void;
  characteristicSelected: (characteristic: Characteristic) => void;
  initialSelectedCharacteristic?: Characteristic;
  enabledIntegrations?: Record<Integration, boolean>;
  hiddenCharacteristics?: Set<CustomerCharacteristicType>;
}

export const SegmentCharacteristicSelectModal = memo(
  function SegmentCharacteristicSelectModal({
    isOpen,
    onModalCloseRequested,
    initialSelectedCharacteristic,
    characteristicSelected,
    enabledIntegrations,
    hiddenCharacteristics,
  }: SegmentCharacteristicSelectModalProps) {
    const filteredCategories = useMemo(() => {
      let categories = schemaToCategories(
        segmentDataStructureTypeToDataStructure[
          "customer-characteristic"
        ] as SegmentDataStructure<CustomerCharacteristicType>,
      );

      if (!enabledIntegrations?.[Integration.LOYALTY_LION]) {
        categories = categories.filter(
          (category) =>
            category.name !==
            segmentCategoryToFriendly[SegmentConditionCategory.LOYALTY_LION],
        );
      }

      if (hiddenCharacteristics?.size) {
        categories = categories.map((category) => ({
          ...category,
          items: category.items.filter(
            (item) => !hiddenCharacteristics.has(item.type),
          ),
        }));
      }

      return categories;
    }, [enabledIntegrations, hiddenCharacteristics]);

    return (
      <SegmentFieldSelectModal
        categories={filteredCategories}
        conditionSelected={characteristicSelected}
        initialSelectedCondition={initialSelectedCharacteristic}
        isOpen={isOpen}
        onModalCloseRequested={onModalCloseRequested}
        submitText="Use characteristic"
        title="Select characteristic"
      />
    );
  },
);
